/**
 * Native Storage Adapter (Strategy Pattern)
 * 
 * Configurable adapter that can use either localStorage or sessionStorage.
 * This eliminates code duplication between local and session storage adapters.
 * 
 * Pros:
 * - Fast (synchronous, no network)
 * - Configurable (localStorage or sessionStorage)
 * - Large capacity (usually 5-10MB)
 * 
 * Cons:
 * - Blocked in third-party iframes (Safari, Firefox strict mode)
 * - Blocked in private browsing (Safari)
 * - Synchronous API (blocking, but fast)
 */

import type { StorageAdapter } from '../types';
import { testLocalStorage, testSessionStorage } from '../utils/detector';

/**
 * Storage type configuration
 */
type StorageType = 'local' | 'session';

/**
 * Native storage adapter with configurable strategy
 */
class NativeStorageAdapter implements StorageAdapter {
  readonly name: string;
  private readonly storage: Storage;
  private readonly storageType: StorageType;

  constructor(storageType: StorageType = 'local') {
    this.storageType = storageType;
    this.storage = storageType === 'local' ? localStorage : sessionStorage;
    this.name = storageType === 'local' ? 'native' : 'session';
  }

  async get(key: string): Promise<string | null> {
    try {
      return this.storage.getItem(key);
    } catch (error) {
      if (import.meta.env.DEV) {
        console.warn(`[${this.storageType}Storage] Failed to get:`, key, error);
      }
      return null;
    }
  }

  async set(key: string, value: string): Promise<void> {
    try {
      this.storage.setItem(key, value);
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error(`[${this.storageType}Storage] Failed to set:`, key, error);
      }
      throw new Error(`Failed to save to ${this.storageType}Storage: ${error}`);
    }
  }

  async remove(key: string): Promise<void> {
    try {
      this.storage.removeItem(key);
    } catch (error) {
      if (import.meta.env.DEV) {
        console.warn(`[${this.storageType}Storage] Failed to remove:`, key, error);
      }
    }
  }

  async clear(): Promise<void> {
    try {
      this.storage.clear();
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error(`[${this.storageType}Storage] Failed to clear:`, error);
      }
      throw new Error(`Failed to clear ${this.storageType}Storage: ${error}`);
    }
  }

  async keys(): Promise<string[]> {
    try {
      return Object.keys(this.storage);
    } catch (error) {
      if (import.meta.env.DEV) {
        console.warn(`[${this.storageType}Storage] Failed to get keys:`, error);
      }
      return [];
    }
  }

  async isAvailable(): Promise<boolean> {
    return this.storageType === 'local' ? testLocalStorage() : testSessionStorage();
  }
}

/**
 * Pre-configured instances
 */
export const localStorageAdapter = new NativeStorageAdapter('local');
export const sessionStorageAdapter = new NativeStorageAdapter('session');

/**
 * Backward compatibility alias
 */
export const nativeStorageAdapter = localStorageAdapter;
